import langs from 'langs';

import countries from './countries/en';
import supportedLanguages from './supportedLanguages';
import { Language, Languages, SupportedLanguage } from './types';
import { LANGUAGE_MAP } from './languageMap';

const KNOWN_SCRIPTS = new Set(['latn', 'cyrl', 'hans', 'hant', 'arab', 'hebr', 'deva', 'thai']);

const ACCEPT_LANGUAGE_MAP = LANGUAGE_MAP;

const ISO_639_1 = new Set(langs.all().map((l: { '1': string }) => l['1']));
const ISO_3166_1 = new Set(Object.keys(countries));

// Checks if a URL segment looks like a locale we don't support.
// We need this to strip things like 'es-co' or 'ur-in' from paths
// without accidentally stripping CMS slugs like 'co-op' or 'log-in'.
//
//   'es-co'             → true   (real language + real country)
//   'sr-latn'           → true   (real language + known script)
//   'co-op'             → false  ('op' is not a country)
//   'log-in'            → false  ('log' is not a language)
//   'is-my-trip-covered'→ false  (too many parts)
function looksLikeLocale(segment: string): boolean {
  const subtags = segment.split('-');
  if (subtags.length !== 2) return false;

  const [primary, suffix] = subtags;

  // 2-letter codes: check the full ISO 639-1 list (catches ur, hi, bn, etc.)
  // 3-letter codes: only check our own list to avoid false positives like 'log', 'opt'
  const isValidLanguage =
    (primary.length === 2 && ISO_639_1.has(primary)) ||
    (primary.length === 3 && Boolean(supportedLanguages[primary as SupportedLanguage]));

  // Second part must be a real country (co, tw), a numeric region (419), or a script (latn)
  const isValidSuffix =
    (/^[a-z]{2}$/.test(suffix) && ISO_3166_1.has(suffix.toUpperCase())) ||
    /^[0-9]{3}$/.test(suffix) ||
    KNOWN_SCRIPTS.has(suffix);

  return isValidLanguage && isValidSuffix;
}

// Builds a redirect URL with the right locale in front.
//
// Example: acceptLocale='en', pathname='/es-co/fnol/details', basePath='/claims'
//
//   1. First segment is 'es-co' — looks like a locale, strip it
//      path goes from '/es-co/fnol/details' → '/fnol/details'
//   2. Pick locale: 'es-co' isn't supported, but 'es' is → use 'es'
//   3. Put it back together: '/es' + '/claims' + '/fnol/details'
//   Result: '/es/claims/fnol/details'
//
// If the first segment isn't a locale (e.g. a claim ref like 'JG9L6-Q8FYX-CLA'),
// nothing is stripped and acceptLocale is used as-is.
//
// availableLocales: the set of locales that have translations (languages.json).
// When provided, a path locale is only preserved if it exists in this set.
// This prevents redirect loops for locales like 'zh' that are recognised as
// valid URL segments (supportedLanguages) but have no translations — the app
// would reject them and redirect again, looping forever.
export function getNewURLWithLocale(
  acceptLocale: string,
  pathname: string,
  origin: string,
  search?: string,
  hash?: string,
  basePath?: string,
  availableLocales?: Record<string, unknown>,
) {
  const firstSegment = pathname.split('/')[1] || '';
  const tag = firstSegment.toLowerCase() as SupportedLanguage;
  const isSupported = Boolean(supportedLanguages[tag]);

  const hasTranslations = (code: string) => (availableLocales ? Boolean(availableLocales[code]) : true);

  let locale = acceptLocale;
  let path = pathname;

  // If the first segment is a locale (or looks like one), strip it and pick
  // the best locale to replace it with:
  //   'es-mx' (we support it, has translations)  → locale = 'es-mx'
  //   'zh'    (supported but NO translations)     → normalize via ACCEPT_LANGUAGE_MAP → 'zh-hans'
  //   'es-co' (we support 'es')                   → locale = 'es'
  //   'zh-tw' (maps to 'zh-hant' via map)         → locale = 'zh-hant'
  //   'ur-in' (we don't support ur)               → locale stays as acceptLocale
  if (isSupported || looksLikeLocale(tag)) {
    path = pathname.slice(1 + firstSegment.length);

    if (isSupported) {
      if (hasTranslations(tag)) {
        locale = tag;
      } else {
        const mapped = ACCEPT_LANGUAGE_MAP[tag];
        if (mapped && hasTranslations(mapped)) {
          locale = mapped;
        }
      }
    } else {
      const mapped = ACCEPT_LANGUAGE_MAP[tag];
      if (mapped && hasTranslations(mapped)) {
        locale = mapped;
      } else {
        const primary = tag.split('-')[0] as SupportedLanguage;
        if (supportedLanguages[primary] && hasTranslations(primary)) {
          locale = primary;
        }
      }
    }
  }

  // Next.js bug #65639 strips '/claims' from the pathname before we see it.
  // basePath puts it back. If the path still has '/claims' at the start, remove
  // it here so we don't end up with '/es/claims/claims/fnol/details'.
  const base = basePath || '';
  if (base) {
    path = path.replace(new RegExp(`^${base}`), '');
  }

  const suffix = path === '/' ? '' : path;
  return new URL(`/${locale}${base}${suffix}${search || ''}${hash || ''}`, origin);
}

// Picks the best language from the browser's Accept-Language header.
//
// Example: header = 'zh-CN,zh;q=0.9,en;q=0.8'
//   1. Try 'zh-cn' → normalize to 'zh-hans' → found in languages.json → done
//
// Without the normalize step, 'zh-cn' doesn't match any key in languages.json
// (we use 'zh-hans' not 'zh-cn'), so it would skip Chinese and return English.
export function getLanguageFromAcceptLanguageHeader(
  acceptLanguageHeader: string,
  languages: Languages,
  userCountry: string,
  userContinent: string,
) {
  // Convert browser codes to our keys ('zh-cn' → 'zh-hans', 'nb' → 'no'),
  // then look up in languages.json. Also handles geo overrides like en + US → en-us.
  function normalizeAndMatch(tag: string): string | null {
    const normalized = ACCEPT_LANGUAGE_MAP[tag] ?? tag;

    if (normalized.startsWith('pt') && userCountry === 'BR' && languages['pt-br']) {
      return 'pt-br';
    }
    if (normalized === 'en' && (userCountry === 'US' || userContinent === 'SA') && languages['en-us']) {
      return 'en-us';
    }
    if (languages[normalized as Language]) {
      return normalized;
    }
    return null;
  }

  // Go through the header left to right (most preferred first).
  // For each entry try the full tag first ('zh-cn'), then just the language part ('zh').
  const tagPattern = /((([a-zA-Z]+)(-[a-zA-Z0-9]+){0,2})(;q=([0-1]\.[0-9]+)?)?)/g;
  let m;
  do {
    m = tagPattern.exec(acceptLanguageHeader.toLocaleLowerCase());

    const withRegion = m?.[2];
    if (withRegion) {
      const match = normalizeAndMatch(withRegion);
      if (match) return match;
    }

    const withoutRegion = m?.[3];
    if (withoutRegion) {
      const match = normalizeAndMatch(withoutRegion);
      if (match) return match;
    }
  } while (m !== null);

  return 'en';
}
